'use client';
import Link from 'next/link';
import { useState, useSyncExternalStore } from 'react';
import { DISCORD_INVITE_URL, FACEBOOK_GROUP_URL, type GameType } from '@puzzlio/core';
import ContactForm from '@/app/components/ContactForm';
import { useDetectedBrowser } from '@/app/hooks/useDetectedBrowser';
import { useIntroAwareGameURL } from '@/app/hooks/useIntroAwareGameURL';
import { GAME_CONFIG, PLAYABLE_GAME_TYPES } from '@/app/games/config';
import { cn } from '@/app/lib/utils';
import { YoutubeIcon, FacebookIcon, InstagramIcon, TikTokIcon, DiscordIcon } from '@puzzlio/ui';
import { BasicCookieBanner } from '@/app/components/cookie/basic-cookie-banner';

const footerLinkClass = 'flex items-center gap-2 h-[38px] text-base font-semibold text-gray-5 hover:underline';
const sectionHeadingClass = 'text-lg font-bold uppercase text-gray-5 h-9 flex items-center font-quicksand';
const iconClass = 'size-[22px] text-gray-5';

const GAME_TYPES = PLAYABLE_GAME_TYPES;

/** The footer's background, as the `bg-[#fcfbfa]` below draws it. */
export const FOOTER_BACKGROUND = '#fcfbfa';

// Game links route into the player's intro sequence while they're still onboarding, and to the weekly
// puzzle once they've graduated — matching the GameCard CTA behaviour.
function FooterGameLink({ gameType }: { gameType: GameType }) {
  const { label, icon: Icon } = GAME_CONFIG[gameType];
  const href = useIntroAwareGameURL(gameType);
  return (
    <Link prefetch={false} href={href} className={footerLinkClass}>
      <Icon className="size-[34px]" />
      <span>{label}</span>
    </Link>
  );
}

const emptySubscribe = () => () => {};
const detectStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export function SiteFooter({ className, contentClassName }: { className?: string; contentClassName?: string }) {
  const [showContactForm, setShowContactForm] = useState(false);
  const browser = useDetectedBrowser();
  // Read on the client only; the server (and first hydration render) sees false.
  const isStandalone = useSyncExternalStore(emptySubscribe, detectStandalone, () => false);
  const showAddToHomeScreen = browser !== 'unknown' && !isStandalone;

  return (
    <footer
      // No spacing of its own above: each page decides how far its content sits from the footer.
      className={cn('relative z-30 mt-auto flex w-full flex-col items-center', className)}
      data-ph-capture-attribute-nav="footer"
    >
      <BasicCookieBanner />
      <div className={cn('flex w-full flex-col items-center bg-[#fcfbfa] px-5', contentClassName)}>
        <div className="w-full max-w-[740px]">
          <div className="flex flex-wrap justify-start gap-x-16 gap-y-4 pt-5 pb-5">
            <div className="flex flex-col">
              <h3 className={sectionHeadingClass}>OUR GAMES</h3>
              {GAME_TYPES.map((gameType) => (
                <FooterGameLink key={gameType} gameType={gameType} />
              ))}
            </div>

            <div className="flex flex-wrap gap-x-10 gap-y-3">
              <div className="flex flex-col">
                <h3 className={sectionHeadingClass}>LEARN MORE</h3>
                <Link prefetch={false} href="/about" className={footerLinkClass}>
                  About
                </Link>
                <Link prefetch={false} href="/blog" className={footerLinkClass}>
                  Blog
                </Link>
                <Link
                  prefetch={false}
                  href="https://shop.inkwellgames.com"
                  className={footerLinkClass}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Shop
                </Link>
                <Link prefetch={false} href="/faq" className={footerLinkClass}>
                  FAQ
                </Link>
                <button onClick={() => setShowContactForm(true)} className={footerLinkClass}>
                  Contact Us
                </button>
                {showAddToHomeScreen && (
                  <Link prefetch={false} href="/add-to-home-screen" className={footerLinkClass}>
                    Add to Home Screen
                  </Link>
                )}
              </div>

              <div className="flex flex-col">
                <h3 className={sectionHeadingClass}>COMMUNITY</h3>
                <Link
                  prefetch={false}
                  href={FACEBOOK_GROUP_URL}
                  className={footerLinkClass}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FacebookIcon className={iconClass} />
                  <span>Inkwell Insiders</span>
                </Link>
                <Link
                  prefetch={false}
                  href={DISCORD_INVITE_URL}
                  className={footerLinkClass}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <DiscordIcon className={iconClass} />
                  <span>Discord</span>
                </Link>
                <Link
                  prefetch={false}
                  href="https://www.youtube.com/@Inkwell-Games"
                  className={footerLinkClass}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <YoutubeIcon className={iconClass} />
                  <span>YouTube</span>
                </Link>
                <Link
                  prefetch={false}
                  href="https://www.instagram.com/inkwellgames/"
                  className={footerLinkClass}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <InstagramIcon className={iconClass} />
                  <span>Instagram</span>
                </Link>
                <Link
                  prefetch={false}
                  href="https://www.tiktok.com/@inkwell_games"
                  className={footerLinkClass}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <TikTokIcon className={iconClass} />
                  <span>TikTok</span>
                </Link>
                <Link
                  prefetch={false}
                  href="https://www.facebook.com/InkwellGames/"
                  className={footerLinkClass}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FacebookIcon className={iconClass} />
                  <span>Facebook</span>
                </Link>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-start gap-x-1 gap-y-1.5 pt-2.5 pb-8 text-sm font-semibold text-gray-400">
            {/* A page built last year still says so in its HTML; the client's year replaces it. */}
            <span suppressHydrationWarning>© {new Date().getFullYear()} Inkwell Games Inc.</span>
            <span className="text-xs">•</span>
            <Link prefetch={false} href="/terms" className="hover:text-black/80 hover:underline">
              Terms of Use
            </Link>
            <span className="text-xs">•</span>
            <Link prefetch={false} href="/privacy" className="hover:text-black/80 hover:underline">
              Privacy Policy
            </Link>
            <span className="text-xs">•</span>
            <Link prefetch={false} href="/cookies" className="hover:text-black/80 hover:underline">
              Cookies
            </Link>
          </div>

          <div className="pb-8 text-sm font-semibold text-gray-400">
            A Carina{' '}
            <Link
              prefetch={false}
              href="https://thinkinggames.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-black/80 hover:underline"
            >
              Thinking Games Initiative
            </Link>
          </div>
        </div>
      </div>

      {showContactForm && <ContactForm onClose={() => setShowContactForm(false)} />}
    </footer>
  );
}
